import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import { Translate } from 'react-localize-redux';
import { trackMixpanelEvent } from 'utils/general';
import css from '../signup/style.css';
import partnerCss from './style.css';
import TextField from '../components/molecules/TextField';
import Button from '../components/atoms/Button';
import Tooltip from '../components/molecules/Tooltip';
import Icon, { IconSizes, IconTypes } from '../components/foundations/Icon';
import WarningMessage from '../components/utilities/WarningMessage';
import ThankYou from '../signup/components/thankYou';
import ShowPasswordIcon from '../resources/svg/visible.svg';
import HidePasswordIcon from '../resources/svg/hide.svg';
import { RECAPTCHA_SIGNUP_SITE_KEY } from '../signup/consts';
import {
    validateFullName,
    validateEmail,
    validatePassword,
    validateCompanyName,
    isValidPartnerForm,
    PARTNER_ERROR_CODES,
    HAS_INTEGRATION_OPTIONS,
    WORKING_WITH_ADVERTISERS_OPTIONS,
} from './utils';
import PartnerSidebar from './PartnerSidebar';
import BlockingPage, { BLOCKING_NOT_WORKING, BLOCKING_HAS_INTEGRATION } from './BlockingPage';

import { WIZARD_SESSION_KEY } from '../accountTypeWizard/consts';

const MIXPANEL_PREFIX = 'Partner Sign Up';
const TRANSLATE_PREFIX = 'STATIC.PAGES.PARTNER_SIGNUP';
const SIGNUP_TRANSLATE_PREFIX = 'STATIC.PAGES.SIGNUP';
const AGENCY_VS_PARTNER_ARTICLE_URL = 'https://support.singular.net/hc/en-us/articles/48174574555931-Difference-between-Agency-Accounts-and-Partner-Portal';

let pageLoadTime;

function PartnerSignup({
    onSubmitClick,
    onResendClick,
    status,
    errorCode,
    isLoading,
    emailSent,
}) {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        passwordConfirmation: '',
        companyName: '',
        workingWithAdvertisers: '',
        hasLiveIntegration: '',
        passwordTextFieldType: 'password',
        confirmPasswordTextFieldType: 'password',
        recaptchaToken: null,
    });
    const [validations, setValidations] = useState({});
    const [blockingPage, setBlockingPage] = useState(null);

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (['fullName', 'email', 'password', 'passwordConfirmation', 'companyName'].includes(field)) {
            setValidations(v => ({ ...v, [field]: null }));
        }
    };

    useEffect(() => {
        pageLoadTime = new Date();
        window.mixpanel.track('partner_sign_up_page_load');
    }, []);

    // Check eligibility on dropdown change and show blocking page
    useEffect(() => {
        if (formData.workingWithAdvertisers === WORKING_WITH_ADVERTISERS_OPTIONS.NO) {
            setBlockingPage(BLOCKING_NOT_WORKING);
        } else if (formData.hasLiveIntegration === HAS_INTEGRATION_OPTIONS.YES || formData.hasLiveIntegration === HAS_INTEGRATION_OPTIONS.NOT_SURE) {
            setBlockingPage(BLOCKING_HAS_INTEGRATION);
        }
    }, [formData.workingWithAdvertisers, formData.hasLiveIntegration]);

    const onSignupClick = () => {
        const wizardAnswers = JSON.parse(sessionStorage.getItem(WIZARD_SESSION_KEY) || 'null');
        if (wizardAnswers) sessionStorage.removeItem(WIZARD_SESSION_KEY);

        const newValidations = {
            fullName: validateFullName(formData.fullName),
            email: validateEmail(formData.email),
            password: validatePassword(formData.password),
            passwordConfirmation: formData.password !== formData.passwordConfirmation ? `${TRANSLATE_PREFIX}.PASSWORDS_DO_NOT_MATCH` : null,
            companyName: validateCompanyName(formData.companyName),
        };

        setValidations(newValidations);

        if (isValidPartnerForm(newValidations) && formData.recaptchaToken) {
            trackMixpanelEvent(MIXPANEL_PREFIX, 'Submit', {
                signup_email: formData.email,
                company_name: formData.companyName,
                time_to_submit: (new Date() - pageLoadTime) / 1000,
                success: 'true',
                ...(wizardAnswers || {}),
            });
            onSubmitClick({
                fullName: formData.fullName,
                email: formData.email,
                password: formData.password,
                companyName: formData.companyName,
                workingWithAdvertisers: formData.workingWithAdvertisers === WORKING_WITH_ADVERTISERS_OPTIONS.YES,
                hasLiveIntegration: formData.hasLiveIntegration,
                recaptchaToken: formData.recaptchaToken,
            });
        } else {
            if (!formData.recaptchaToken) {
                setValidations(v => ({ ...v, recaptcha: `${SIGNUP_TRANSLATE_PREFIX}.INVALID_FIELD_RECAPTCHA_TOKEN` }));
            }
            trackMixpanelEvent(MIXPANEL_PREFIX, 'Submit', {
                signup_email: formData.email,
                company_name: formData.companyName,
                time_to_submit: (new Date() - pageLoadTime) / 1000,
                success: 'false',
                ...(wizardAnswers || {}),
            });
        }
    };

    const onReCaptchaChange = token => {
        updateField('recaptchaToken', token);
        setValidations(v => ({ ...v, recaptcha: null }));
    };

    const togglePasswordTextFieldType = () => {
        updateField('passwordTextFieldType', formData.passwordTextFieldType === 'password' ? 'text' : 'password');
    };

    const toggleConfirmPasswordTextFieldType = () => {
        updateField('confirmPasswordTextFieldType', formData.confirmPasswordTextFieldType === 'password' ? 'text' : 'password');
    };

    const isSuccess = status === 'success';

    if (isSuccess) {
        return (
            <div className={css.signupContainer}>
                <ThankYou
                    onResendClick={onResendClick}
                    isLoading={isLoading}
                    emailSent={emailSent}
                    email={formData.email}
                />
            </div>
        );
    }

    if (blockingPage) {
        return (
            <div className={css.signupContainer}>
                <PartnerSidebar />
                <div className={classNames(css.signupForm, partnerCss.partnerSignupForm)}>
                    <BlockingPage
                        type={blockingPage}
                        onGoBack={() => {
                            updateField('workingWithAdvertisers', '');
                            updateField('hasLiveIntegration', '');
                            setBlockingPage(null);
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={css.signupContainer}>
            <PartnerSidebar />
            <div className={classNames(css.signupForm, partnerCss.partnerSignupForm)}>
                <div className={classNames(css.signupFormInner, partnerCss.partnerSignupFormInner)}>
                    <div className={partnerCss.partnerFormTopSection}>
                        <div className={partnerCss.partnerFormTitle}>
                            <Translate id={`${TRANSLATE_PREFIX}.FORM_TITLE_LINE1`} />
                            <br />
                            <Translate id={`${TRANSLATE_PREFIX}.FORM_TITLE_LINE2`} />
                        </div>
                        <div className={css.singularFormBody}>
                            <WarningMessage
                                show={!!errorCode}
                                showIcon={false}
                                showTypeIcon
                                type="error"
                                message={getServerErrorMessage(errorCode)}
                            />
                            <TextField
                                inputConfig={{ maxLength: 100 }}
                                error={validations.companyName}
                                value={formData.companyName}
                                containerClass={css.textField}
                                label={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_WHATS_COMPANY_NAME`}
                                onChange={val => updateField('companyName', val)}
                                disabled={isLoading}
                            />
                            <TextField
                                inputConfig={{ maxLength: 61 }}
                                error={validations.fullName}
                                value={formData.fullName}
                                containerClass={css.textField}
                                label={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_NAME_FIELD_LABEL`}
                                onChange={val => updateField('fullName', val)}
                                disabled={isLoading}
                            />
                            <TextField
                                inputConfig={{ maxLength: 70 }}
                                error={validations.email}
                                value={formData.email}
                                containerClass={css.textField}
                                label={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_EMAIL_FIELD_LABEL`}
                                onChange={val => updateField('email', val)}
                                disabled={isLoading}
                            />

                            <div className={partnerCss.dropdownField}>
                                <div className={partnerCss.dropdownLabel}>
                                    <Translate id={`${TRANSLATE_PREFIX}.HAS_LIVE_INTEGRATION_LABEL`} />
                                </div>
                                <Translate>
                                    {({ translate }) => (
                                        <select
                                            value={formData.hasLiveIntegration}
                                            onChange={e => updateField('hasLiveIntegration', e.target.value)}
                                            disabled={isLoading}
                                            className={partnerCss.dropdownSelect}
                                        >
                                            <option value="" disabled hidden />
                                            <option value={HAS_INTEGRATION_OPTIONS.YES}>{translate(`${TRANSLATE_PREFIX}.OPTION_YES`)}</option>
                                            <option value={HAS_INTEGRATION_OPTIONS.NO}>{translate(`${TRANSLATE_PREFIX}.OPTION_NO`)}</option>
                                            <option value={HAS_INTEGRATION_OPTIONS.NOT_SURE}>{translate(`${TRANSLATE_PREFIX}.OPTION_NOT_SURE`)}</option>
                                        </select>
                                    )}
                                </Translate>
                            </div>

                            <div className={partnerCss.dropdownField}>
                                <div className={partnerCss.dropdownLabel}>
                                    <Translate id={`${TRANSLATE_PREFIX}.WORKING_WITH_CLIENTS_LABEL`} />
                                </div>
                                <Translate>
                                    {({ translate }) => (
                                        <select
                                            value={formData.workingWithAdvertisers}
                                            onChange={e => updateField('workingWithAdvertisers', e.target.value)}
                                            disabled={isLoading}
                                            className={partnerCss.dropdownSelect}
                                        >
                                            <option value="" disabled hidden />
                                            <option value={WORKING_WITH_ADVERTISERS_OPTIONS.YES}>{translate(`${TRANSLATE_PREFIX}.OPTION_YES`)}</option>
                                            <option value={WORKING_WITH_ADVERTISERS_OPTIONS.NO}>{translate(`${TRANSLATE_PREFIX}.OPTION_NO`)}</option>
                                        </select>
                                    )}
                                </Translate>
                            </div>

                            <div className={css.passwordField}>
                                <Tooltip
                                    interactive
                                    wrapperClass={classNames(css.tooltip, css.passwordTooltip)}
                                    titleTranslationKey={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_PASSWORD_TOOLTIP`}
                                >
                                    <Icon type={IconTypes.MINGCUTE} name="question-line" size={IconSizes.MEDIUM} />
                                </Tooltip>
                                {formData.password && formData.passwordTextFieldType === 'password' && (
                                    <Tooltip
                                        interactive
                                        titleTranslationKey={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_SHOW_PASSWORD_TOOLTIP`}
                                        style={{ position: 'absolute', right: 0 }}
                                        position="left"
                                    >
                                        <ShowPasswordIcon
                                            className={css.showPasswordIcon}
                                            onClick={togglePasswordTextFieldType}
                                        />
                                    </Tooltip>
                                )}
                                {formData.password && formData.passwordTextFieldType === 'text' && (
                                    <HidePasswordIcon
                                        className={css.hidePasswordIcon}
                                        onClick={togglePasswordTextFieldType}
                                    />
                                )}
                                <Tooltip
                                    interactive
                                    titleTranslationKey={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_PASSWORD_TOOLTIP`}
                                    trigger="click"
                                    position="top-start"
                                >
                                    <TextField
                                        inputConfig={{ maxLength: 50 }}
                                        error={validations.password}
                                        value={formData.password}
                                        containerClass={css.textField}
                                        label={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_PASSWORD_FIELD_LABEL`}
                                        onChange={val => updateField('password', val)}
                                        type={formData.passwordTextFieldType}
                                        disabled={isLoading}
                                    />
                                </Tooltip>
                            </div>
                            <div className={css.passwordField}>
                                {formData.passwordConfirmation && formData.confirmPasswordTextFieldType === 'password' && (
                                    <ShowPasswordIcon
                                        className={css.showPasswordIcon}
                                        onClick={toggleConfirmPasswordTextFieldType}
                                    />
                                )}
                                {formData.passwordConfirmation && formData.confirmPasswordTextFieldType === 'text' && (
                                    <HidePasswordIcon
                                        className={css.hidePasswordIcon}
                                        onClick={toggleConfirmPasswordTextFieldType}
                                    />
                                )}
                                <TextField
                                    inputConfig={{ maxLength: 50 }}
                                    error={validations.passwordConfirmation}
                                    value={formData.passwordConfirmation}
                                    containerClass={css.textField}
                                    label={`${TRANSLATE_PREFIX}.CONFIRMATION_LABEL`}
                                    onChange={val => updateField('passwordConfirmation', val)}
                                    type={formData.confirmPasswordTextFieldType}
                                    disabled={isLoading}
                                />
                            </div>

                            <div className={css.recaptcha}>
                                <ReCAPTCHA sitekey={RECAPTCHA_SIGNUP_SITE_KEY} onChange={onReCaptchaChange} />
                            </div>
                            {validations.recaptcha && (
                                <div className={css.errorMessage}>{validations.recaptcha}</div>
                            )}

                            <div className={css.termsText}>
                                <span><Translate id={`${TRANSLATE_PREFIX}.TERMS_TEXT`} /></span>
                                <a href="https://www.singular.net/terms/" target="_blank" rel="noopener noreferrer">
                                    <Translate id={`${TRANSLATE_PREFIX}.TERMS_LINK_TEXT`} />
                                </a>.
                            </div>

                            <div className={css.submit}>
                                <Button
                                    onClick={onSignupClick}
                                    type="primary"
                                    level="level1"
                                    text={`${TRANSLATE_PREFIX}.SUBMIT_BUTTON`}
                                    showSpinner={isLoading}
                                    className={css.submitButton}
                                    textClassName={css.submitButtonText}
                                />
                            </div>
                            <div className={css.footerSeparator} />
                            <div className={css.loginLink}>
                                <span><Translate id={`${TRANSLATE_PREFIX}.LOGIN_LINK`} /></span>
                                <a href="/login"><Translate id={`${TRANSLATE_PREFIX}.LOGIN_LINK_TEXT`} /></a>
                            </div>
                            <div className={partnerCss.agencyLink}>
                                <span><Translate id={`${TRANSLATE_PREFIX}.AGENCY_LINK`} /></span>
                                <a href={AGENCY_VS_PARTNER_ARTICLE_URL} target="_blank" rel="noopener noreferrer"><Translate id={`${TRANSLATE_PREFIX}.AGENCY_LINK_TEXT`} /></a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function getServerErrorMessage(errorCode) {
    const TRANSLATE_ERROR_PREFIX = `${TRANSLATE_PREFIX}.`;
    const messages = {
        [PARTNER_ERROR_CODES.ERROR_EMAIL_IN_BLACK_LIST]: `${TRANSLATE_ERROR_PREFIX}ERROR_EMAIL_IN_BLACK_LIST`,
        [PARTNER_ERROR_CODES.ERROR_REJECTED_EMAIL]: `${TRANSLATE_ERROR_PREFIX}ERROR_REJECTED_EMAIL`,
        [PARTNER_ERROR_CODES.ERROR_RECAPTCHA_VALIDATION_FAILED]: `${TRANSLATE_ERROR_PREFIX}ERROR_RECAPTCHA_VALIDATION_FAILED`,
    };
    return messages[errorCode] || errorCode || '';
}

PartnerSignup.propTypes = {
    onSubmitClick: PropTypes.func.isRequired,
    onResendClick: PropTypes.func.isRequired,
    status: PropTypes.string,
    errorCode: PropTypes.string,
    isLoading: PropTypes.bool.isRequired,
    emailSent: PropTypes.bool.isRequired,
};

export default PartnerSignup;
